import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, Stack, Switch, TextField, Button, Divider, Alert, MenuItem,
  Chip, Skeleton, Collapse, alpha, useTheme,
} from '@mui/material';
import {
  LayoutGrid, Server, Sparkles, SlidersHorizontal, Info, EyeOff, BellRing,
} from 'lucide-react';
import axios from '../utils/axios';
import { useFeatureFlags } from '../contexts/FeatureFlagsProvider';
import PageHeader from '../components/common/PageHeader';

/* ─────────────────────────── shared building blocks ──────────────────────*/

// A titled section card with an icon, one-line purpose and helper text.
const SectionCard = ({ icon: Icon, color, title, purpose, badge, right, children }) => {
  const theme = useTheme();
  const c = color || theme.palette.primary.main;
  return (
    <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 4 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2, flexShrink: 0,
            bgcolor: alpha(c, 0.14), color: c,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon size={20} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
              {badge}
            </Stack>
            {purpose && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {purpose}
              </Typography>
            )}
          </Box>
        </Stack>
        {right && <Box sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}>{right}</Box>}
      </Stack>
      {children}
    </Card>
  );
};

// One labelled toggle row: title + description + the switch.
const ToggleRow = ({ title, description, checked, onChange, disabled, chip }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1.25 }}>
    <Box sx={{ pr: 2, opacity: disabled ? 0.5 : 1 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
        {chip}
      </Stack>
      {description && <Typography variant="caption" color="text.secondary">{description}</Typography>}
    </Box>
    <Switch checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
  </Stack>
);

const ServerChip = () => (
  <Chip size="small" label="Saves instantly" color="success" variant="outlined"
    sx={{ height: 20, fontSize: 10, fontWeight: 800 }} />
);

/* ─────────────────────────── Modules & Navigation ────────────────────────
 * Disabling a module hides its sidebar tab AND blocks its page (route). Driven
 * by the shared FeatureFlags context so the sidebar updates the moment you flip
 * a switch — no reload. Dashboard & Settings are intentionally not listed so
 * you can never hide your way out of the controls.
 */
const ModulesCard = () => {
  const ff = useFeatureFlags();
  const theme = useTheme();
  if (!ff || ff.loading) return <Card sx={{ p: 3, borderRadius: 4 }}><Skeleton height={240} /></Card>;

  const { moduleCatalogue, modules, update } = ff;
  const enabled = (key) => modules[key] !== false;
  const toggle = (key, v) => update({ modules_enabled: { [key]: v } });

  // Group by the catalogue's `group` field, preserving first-seen order.
  const groups = [];
  moduleCatalogue.forEach((m) => {
    let g = groups.find((x) => x.name === m.group);
    if (!g) { g = { name: m.group, items: [] }; groups.push(g); }
    g.items.push(m);
  });

  const hiddenCount = moduleCatalogue.filter((m) => !enabled(m.key)).length;

  return (
    <SectionCard
      icon={LayoutGrid}
      color={theme.palette.primary.main}
      title="Modules & Navigation"
      purpose="Turn whole sections of the admin on or off. Switching one off hides its sidebar tab and blocks its page."
      badge={<ServerChip />}
      right={hiddenCount > 0 ? <Chip size="small" color="warning" icon={<EyeOff size={13} />} label={`${hiddenCount} hidden`} sx={{ fontWeight: 800 }} /> : null}
    >
      <Alert severity="info" icon={<Info size={18} />} sx={{ mb: 2, borderRadius: 2 }}>
        <b>How to use:</b> keep only the modules your team actually works in. A hidden module's tab disappears and
        its page redirects to the Dashboard — useful to declutter the panel and limit what staff can reach.
        <b> Dashboard</b> and <b>Settings</b> always stay available.
      </Alert>

      <Stack spacing={2.5}>
        {groups.map((g) => (
          <Box key={g.name}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, color: 'text.disabled', mb: 0.5 }}>
              {g.name}
            </Typography>
            <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, px: 1.5 }}>
              {g.items.map((m, i) => (
                <React.Fragment key={m.key}>
                  {i > 0 && <Divider />}
                  <ToggleRow
                    title={m.label}
                    description={m.description}
                    checked={enabled(m.key)}
                    onChange={(v) => toggle(m.key, v)}
                  />
                </React.Fragment>
              ))}
            </Box>
          </Box>
        ))}
      </Stack>
    </SectionCard>
  );
};

/* ─────────────────────────── Background Jobs (crons) ──────────────────────*/
const SystemJobsCard = () => {
  const ff = useFeatureFlags();
  const theme = useTheme();
  if (!ff || ff.loading) return <Card sx={{ p: 3, borderRadius: 4 }}><Skeleton height={220} /></Card>;

  const { cronCatalogue, crons, update } = ff;
  const enabled = (key) => crons[key] !== false;
  const toggle = (key, v) => update({ crons_enabled: { [key]: v } });
  const offCount = cronCatalogue.filter((c) => !enabled(c.key)).length;

  return (
    <SectionCard
      icon={Server}
      color={theme.palette.warning.main}
      title="Background Jobs"
      purpose="Scheduled tasks that run on the server. Turn the heavy ones off to cut server load."
      badge={<ServerChip />}
      right={offCount > 0 ? <Chip size="small" color="warning" label={`${offCount} off`} sx={{ fontWeight: 800 }} /> : null}
    >
      <Alert severity="info" icon={<Info size={18} />} sx={{ mb: 2, borderRadius: 2 }}>
        <b>How to use:</b> a disabled job stays scheduled but skips all work instantly — no database sweeps, API
        calls or emails — so it stops consuming resources. Re-enable any time; the change takes effect within seconds.
        Some jobs also require their server env flag to be on.
      </Alert>

      <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, px: 1.5 }}>
        {cronCatalogue.map((c, i) => (
          <React.Fragment key={c.key}>
            {i > 0 && <Divider />}
            <ToggleRow
              title={c.label}
              description={c.description}
              checked={enabled(c.key)}
              onChange={(v) => toggle(c.key, v)}
              chip={<Chip size="small" label={c.schedule} variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
            />
          </React.Fragment>
        ))}
      </Box>
    </SectionCard>
  );
};

/* ─────────────────────────── Activity Notifications ───────────────────────*/
const NotificationsCard = () => {
  const ff = useFeatureFlags();
  const theme = useTheme();
  if (!ff || ff.loading) return <Card sx={{ p: 3, borderRadius: 4 }}><Skeleton height={220} /></Card>;

  const { notificationCatalogue, notifications, update } = ff;
  const enabled = (key) => notifications[key] !== false;
  const toggle = (key, v) => update({ notifications_enabled: { [key]: v } });
  const offCount = notificationCatalogue.filter((n) => !enabled(n.key)).length;

  return (
    <SectionCard
      icon={BellRing}
      color="#0288d1"
      title="Activity Notifications"
      purpose="Choose which new-activity events alert admins — via PWA/OS push and the in-app bell."
      badge={<ServerChip />}
      right={offCount > 0 ? <Chip size="small" color="warning" label={`${offCount} muted`} sx={{ fontWeight: 800 }} /> : null}
    >
      <Alert severity="info" icon={<Info size={18} />} sx={{ mb: 2, borderRadius: 2 }}>
        <b>How to use:</b> turning an event off stops both the OS/PWA push and the in-app bell entry for it —
        instantly, no reload. Each admin still enables notifications on their own device via the bell icon in the top bar.
      </Alert>

      <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, px: 1.5 }}>
        {notificationCatalogue.map((n, i) => (
          <React.Fragment key={n.key}>
            {i > 0 && <Divider />}
            <ToggleRow
              title={n.label}
              description={n.description}
              checked={enabled(n.key)}
              onChange={(v) => toggle(n.key, v)}
            />
          </React.Fragment>
        ))}
      </Box>
    </SectionCard>
  );
};

/* ─────────────────────────── SOP Automation ───────────────────────────────*/
const SopAutomationCard = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [s, setS] = useState(null);
  const [rules, setRules] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await axios.get('/admin/sop/settings', { skipNotify: true });
      setS(data.settings);
      setRules(data.rules || []);
    } catch (e) { /* notified */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (partial) => {
    setS((prev) => ({ ...prev, ...partial }));
    try { await axios.put('/admin/sop/settings', partial, { skipNotify: 'success' }); }
    catch (e) { load(); }
  };
  const patchCategory = (k, v) => patch({ categories_enabled: { ...(s.categories_enabled || {}), [k]: v } });
  const patchRule = (k, v) => patch({ rules_enabled: { ...(s.rules_enabled || {}), [k]: v } });
  const catEnabled = (k) => s.categories_enabled?.[k] !== false;
  const ruleEnabled = (k) => s.rules_enabled?.[k] !== false;

  if (loading) return <Card sx={{ p: 3, borderRadius: 4 }}><Skeleton height={200} /></Card>;
  if (!s) return null;

  const CATS = ['social', 'whatsapp', 'seo', 'sales', 'ops', 'content'];

  return (
    <SectionCard
      icon={Sparkles}
      color="#8b5cf6"
      title="SOP Automation"
      purpose="Controls the SOP module's daily & weekly task generation."
      badge={<ServerChip />}
    >
      <Alert severity="info" icon={<Info size={18} />} sx={{ mb: 2, borderRadius: 2 }}>
        <b>How to use:</b> the master switch stops/starts all data-driven SOPs. Use the recurring switch for the
        fixed checklist, and the advanced switches to silence a single channel or rule without turning everything off.
      </Alert>

      <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, px: 1.5 }}>
        <ToggleRow title="Auto-generate SOPs from data"
          description="Master switch for all data-driven (dynamic) SOPs across the module."
          checked={s.dynamic_generation_enabled} onChange={(v) => patch({ dynamic_generation_enabled: v })} />
        <Divider />
        <ToggleRow title="Recurring checklist SOPs"
          description="The fixed daily/weekly routines (static templates)."
          checked={s.static_generation_enabled} onChange={(v) => patch({ static_generation_enabled: v })} />
        <Divider />
        <ToggleRow title="Morning digest"
          description="WhatsApp / push nudge of the day's SOPs (needs delivery setup)."
          checked={s.digest_enabled} onChange={(v) => patch({ digest_enabled: v })} />
      </Box>

      <Button size="small" onClick={() => setShowAdvanced((x) => !x)}
        sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700 }}>
        {showAdvanced ? 'Hide' : 'Show'} per-channel & per-rule switches
      </Button>

      <Collapse in={showAdvanced}>
        <Box sx={{ mt: 1.5, opacity: s.dynamic_generation_enabled ? 1 : 0.6 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, color: 'text.disabled', mb: 1 }}>
            Channels
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {CATS.map((c) => (
              <Chip key={c} label={c} clickable color={catEnabled(c) ? 'primary' : 'default'}
                variant={catEnabled(c) ? 'filled' : 'outlined'}
                onClick={() => patchCategory(c, !catEnabled(c))} sx={{ fontWeight: 700, textTransform: 'capitalize' }} />
            ))}
          </Stack>

          <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, color: 'text.disabled', mt: 2, mb: 1 }}>
            Data rules
          </Typography>
          <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, px: 1.5 }}>
            {rules.map((r, i) => (
              <React.Fragment key={r.key}>
                {i > 0 && <Divider />}
                <ToggleRow title={r.label} description={r.description}
                  checked={ruleEnabled(r.key)} disabled={!s.dynamic_generation_enabled}
                  onChange={(v) => patchRule(r.key, v)} />
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </Collapse>

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <TextField select size="small" label="Default lookback (days)" sx={{ width: 200 }}
          value={s.default_lookback_days} onChange={(e) => patch({ default_lookback_days: Number(e.target.value) })}
          helperText="Window dynamic rules look back over">
          {[7, 14, 30, 60, 90].map((d) => <MenuItem key={d} value={d}>{d} days</MenuItem>)}
        </TextField>
      </Stack>
    </SectionCard>
  );
};

/* ─────────────────────────── Preferences (local browser) ──────────────────*/
const STORAGE_KEY = 'admin_settings_v1';
const defaultSettings = {
  siteName: 'Reseller Bazzar',
  supportEmail: 'support@resellerbazzar.in',
  defaultRange: '30',
  enableLiveChatBadge: true,
  enableSampleData: true,
  dashboardDensity: 'comfortable',
};
const loadLocal = () => {
  if (typeof window === 'undefined') return defaultSettings;
  try { return { ...defaultSettings, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') }; }
  catch { return defaultSettings; }
};

const PreferencesCard = () => {
  const theme = useTheme();
  const [settings, setSettings] = useState(defaultSettings);
  const [saved, setSaved] = useState(false);
  useEffect(() => { setSettings(loadLocal()); }, []);
  const update = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const handleSave = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch { /* ignore */ }
  };

  return (
    <SectionCard
      icon={SlidersHorizontal}
      color={theme.palette.text.secondary}
      title="Preferences"
      purpose="Personal display preferences for this browser. These are saved locally, not on the server."
      right={<Button variant="contained" onClick={handleSave} sx={{ textTransform: 'none', fontWeight: 700 }}>Save</Button>}
    >
      {saved && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>Preferences saved on this device.</Alert>}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            <TextField fullWidth size="small" label="Site name" value={settings.siteName} onChange={(e) => update('siteName', e.target.value)} />
            <TextField fullWidth size="small" label="Support email" type="email" value={settings.supportEmail} onChange={(e) => update('supportEmail', e.target.value)} />
            <TextField fullWidth select size="small" label="Default analytics window"
              value={settings.defaultRange} onChange={(e) => update('defaultRange', e.target.value)}>
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
              <MenuItem value="180">Last 180 days</MenuItem>
            </TextField>
            <TextField fullWidth select size="small" label="Dashboard density"
              value={settings.dashboardDensity} onChange={(e) => update('dashboardDensity', e.target.value)}>
              <MenuItem value="compact">Compact</MenuItem>
              <MenuItem value="comfortable">Comfortable</MenuItem>
              <MenuItem value="spacious">Spacious</MenuItem>
            </TextField>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, px: 1.5 }}>
            <ToggleRow title="Live chat unread badge" description="Show notification badges on the admin nav."
              checked={settings.enableLiveChatBadge} onChange={(v) => update('enableLiveChatBadge', v)} />
            <Divider />
            <ToggleRow title="Sample-data fallback" description="Show mock numbers when the analytics API is unreachable."
              checked={settings.enableSampleData} onChange={(v) => update('enableSampleData', v)} />
          </Box>
        </Grid>
      </Grid>
    </SectionCard>
  );
};

/* ─────────────────────────── Page ─────────────────────────────────────────*/
const SettingsView = () => {
  const ff = useFeatureFlags();
  // The SOP Automation card talks to the SOP module's API; when that module is
  // disabled its API is refused server-side, so hide the card to match.
  const sopEnabled = !ff || ff.loading ? true : ff.isPathEnabled('/sop');

  return (
    <Box>
      <PageHeader
        title="Platform Settings"
        subtitle="Control which modules appear, which background jobs run, automation, and your display preferences."
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'System', href: '/settings' },
          { label: 'Settings' }
        ]}
      />

      <Stack spacing={{ xs: 2, md: 3 }}>
        <ModulesCard />
        <NotificationsCard />
        <SystemJobsCard />
        {sopEnabled && <SopAutomationCard />}
        <PreferencesCard />
      </Stack>
    </Box>
  );
};

export default SettingsView;
